import {FeatureFlags, FeatureFlagValue} from "@bounce/shared/consumer/helpers";
import {Container, Text, IconButton} from "@bounce/shared/ui";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import {Switch, TextField} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
  clearAllOverrides,
  clearOverride,
  FeatureFlagOverrides,
  getStoredOverrides,
  setOverride,
} from "./featureFlagOverrides";

interface FeatureFlagsTabProps {
  featureFlags?: FeatureFlags;
  onOverridesChange: () => void;
}

export const FeatureFlagsTab = ({featureFlags, onOverridesChange}: FeatureFlagsTabProps) => {
  const [search, setSearch] = useState("");
  const [overrides, setOverrides] = useState<FeatureFlagOverrides>(getStoredOverrides);

  const refreshOverrides = useCallback(() => {
    setOverrides(getStoredOverrides());
  }, []);

  useEffect(() => {
    refreshOverrides();
  }, [refreshOverrides]);

  const flagEntries = useMemo(() => {
    if (!featureFlags) return [];
    return Object.entries(featureFlags)
      .filter(([key]) => key.toLowerCase().includes(search.toLowerCase()))
      .sort(([a], [b]) => a.localeCompare(b));
  }, [featureFlags, search]);

  const handleValueChange = (key: string, newValue: FeatureFlagValue) => {
    setOverride(key, newValue);
    refreshOverrides();
    onOverridesChange();
  };

  const handleClearOverride = (key: string) => {
    clearOverride(key);
    refreshOverrides();
    onOverridesChange();
  };

  const handleClearAll = () => {
    clearAllOverrides();
    refreshOverrides();
    onOverridesChange();
  };

  const isOverridden = (key: string): boolean => key in overrides;
  const getEffectiveValue = (key: string, backendValue: FeatureFlagValue): FeatureFlagValue =>
    key in overrides ? overrides[key] : backendValue;

  const overrideCount = Object.keys(overrides).length;

  return (
    <>
      <Container row sx={{justifyContent: "space-between", mb: 2}}>
        <TextField
          fullWidth
          placeholder="Search flags..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{flex: 1, mr: 1}}
        />
        <IconButton onClick={handleClearAll} disabled={overrideCount === 0} size="small" title="Clear all overrides">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Container>

      <Container sx={{maxHeight: 400, overflow: "auto", gap: 0}}>
        {flagEntries.map(([key, flag]) => {
          const overridden = isOverridden(key);
          const effectiveValue = getEffectiveValue(key, flag.value);

          return (
            <Container
              key={key}
              row
              sx={{
                justifyContent: "space-between",
                py: 1,
                px: 1,
                borderBottom: "1px solid #eee",
                bgcolor: overridden ? "#fff3e0" : "transparent",
                width: "100%",
              }}
            >
              <Container sx={{alignItems: "flex-start"}}>
                <Text size={14} sx={{fontFamily: "monospace", fontWeight: overridden ? "bold" : "normal"}}>
                  {key}
                </Text>
                {overridden && (
                  <Text size={12} sx={{color: "#666"}}>
                    Backend: {String(flag.value)}
                  </Text>
                )}
              </Container>
              <Container row sx={{alignItems: "center", gap: 1}}>
                <Switch
                  checked={effectiveValue === true}
                  onChange={(e) => handleValueChange(key, e.target.checked)}
                  size="small"
                />
                {overridden && (
                  <IconButton size="small" onClick={() => handleClearOverride(key)} title="Reset to backend value">
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                )}
              </Container>
            </Container>
          );
        })}
      </Container>

      {flagEntries.length === 0 && (
        <Text size={14} sx={{color: "#666", textAlign: "center", py: 4}}>
          {search ? "No flags match your search" : "No feature flags available"}
        </Text>
      )}
    </>
  );
};
